import { useBrandVisualKit } from 'hooks/queries/workspaces/useBrandVisualKit'
import Styles from './Logos.module.scss'
import { Logo } from './Logo'
import { LogosProps } from './Types'
import { useState } from 'react'
import { BrandMediaFile, BrandMediaType, LogoVariant } from 'types'
import { Chip } from 'components/Chip/Chip'
import { useDeleteBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useDeleteBrandMediaFile'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { useCreateBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useCreateBrandMediaFile'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { toBase64 } from 'flows/common/steps/BrandKitColorsAndFonts/utils'
import { useRegenerateLogoVariant } from 'hooks/queries/useRegenerateLogoVariant'

const Logos = ({
  logos,
  brandVisualKitId,
  showVariantSelector = true,
  variantsToShow = Object.values(LogoVariant),
  alignButtons = 'space-between',
}: {
  logos: BrandMediaFile[]
  brandVisualKitId: number
  showVariantSelector?: boolean
  variantsToShow?: LogoVariant[]
  alignButtons?: 'space-between' | 'flex-end'
}) => {
  const workspaceId = useCurrentWorkspaceId()
  const [currentVariant, setCurrentVariant] = useState<LogoVariant>(variantsToShow[0] || LogoVariant.Original)
  const { uploadImageFiles } = useImportMediaFiles(false)
  const { mutateAsync: deleteBrandMediaFile, isLoading: isDeletingLogo } = useDeleteBrandMediaFile()
  const { mutateAsync: createBrandMediaFile, isLoading: isCreatingLogo } = useCreateBrandMediaFile()
  const { mutateAsync: regenerateLogoVariant, isLoading: isRegeneratingLogo } = useRegenerateLogoVariant({
    brandVisualKitId,
  })
  const queryClient = useQueryClient()
  const originalLogo = logos.find((logo) => logo.logo_variant === LogoVariant.Original)
  const canRegenerate = Boolean(originalLogo)

  const handleRegenerate = async (variant: LogoVariant.Light | LogoVariant.Dark) => {
    if (!originalLogo) {
      return
    }
    await regenerateLogoVariant({ brandVisualKitId, variant, originalLogo })
  }

  const handleDelete = async (logo: BrandMediaFile) => {
    if (isDeletingLogo) {
      return
    }
    await deleteBrandMediaFile({ id: logo.id, brandVisualKitId: logo.brand_visual_kit_id })
  }

  const handleReplace = async (variant: LogoVariant, file: File) => {
    if (isCreatingLogo) {
      return
    }

    const base64Url = await toBase64(file)

    // Optimistic update
    queryClient.setQueryData(
      ['workspaces', workspaceId, 'brand_visual_kits', brandVisualKitId],
      ({ brand_visual_kit }) => {
        let logos = brand_visual_kit.logos.map((logo: BrandMediaFile) => ({
          ...logo,
          media_file: {
            ...logo.media_file,
            source_url: logo.logo_variant === variant ? base64Url : logo.media_file.source_url,
          },
        }))

        if (!logos.find((logo: BrandMediaFile) => logo.logo_variant === variant)) {
          logos.push({
            logo_variant: variant,
            media_file: { source_url: base64Url },
          })
        }

        return {
          brand_visual_kit: {
            ...brand_visual_kit,
            logos,
          },
        }
      },
    )

    const almanacFiles = await uploadImageFiles(Array.from([file]))

    if (!almanacFiles || almanacFiles.length === 0) {
      return
    }

    const currentLogo = logos.find((logo) => logo.logo_variant === variant)

    await createBrandMediaFile({
      brandMediaType: BrandMediaType.LOGO,
      brandVisualKitId,
      mediaFileId: almanacFiles[0].media_file_id as number,
      logoVariant: variant,
      isPrimaryLogo: true,
    })

    if (currentLogo) {
      await handleDelete(currentLogo)
    }
  }

  const isDisabled = isDeletingLogo || isCreatingLogo

  return (
    <div className={Styles.container}>
      {variantsToShow.map((variant) => (
        <ConditionalWrapper
          key={variant}
          condition={variant !== currentVariant}
          wrapper={(children) => (
            <VisuallyHidden key={variant} aria-hidden={true}>
              {children}
            </VisuallyHidden>
          )}
        >
          <div className={Styles.logoContainer}>
            <Logo
              variant={variant}
              logo={logos.find((logo) => logo.logo_variant === variant)}
              onDelete={handleDelete}
              onReplace={handleReplace}
              onRegenerate={
                canRegenerate && variant !== LogoVariant.Original ? () => handleRegenerate(variant) : undefined
              }
              isDisabled={isDisabled}
              isGenerating={isRegeneratingLogo}
              alignButtons={alignButtons}
            />
          </div>
        </ConditionalWrapper>
      ))}

      {showVariantSelector && (
        <div className={Styles.chips}>
          {variantsToShow.map((variant) => (
            <Chip
              key={variant}
              deletable={false}
              aria-checked={variant === currentVariant}
              onChange={() => setCurrentVariant(variant)}
              isSelected={variant === currentVariant}
              size="lg"
              variant="flat"
            >
              {variant}
            </Chip>
          ))}
        </div>
      )}
    </div>
  )
}

const LogosContainer = ({ brandVisualKitId, showVariantSelector, variantsToShow, alignButtons }: LogosProps) => {
  const { data } = useBrandVisualKit({ id: brandVisualKitId })

  if (!data) {
    return null
  }

  const { logos } = data.brand_visual_kit

  return (
    <Logos
      brandVisualKitId={brandVisualKitId}
      logos={logos.map((logo) => ({ ...logo, logo_variant: logo.logo_variant || LogoVariant.Original }))}
      showVariantSelector={showVariantSelector}
      variantsToShow={variantsToShow}
      alignButtons={alignButtons}
    />
  )
}

export { LogosContainer as Logos }
